import { TrashIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useDebounce from '@dashboard/hooks/useDebounce';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { confirm } from '@dashboard/lib/confirm';
import { templateCategoriesKey } from '@dashboard/lib/queryKeys';

import { fetcher, fetchInfinite } from '@dashboard/services/request';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Button from '@dashboard/components/Button';
import { FormElement, Label, TextInput } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';

import Circle from '../components/Circle';
import Table from '../components/Table';

const Payments = () => {
  const { token } = useAuth();
  const history = useHistory();

  const query = useUrlQuery();

  const { searchInput, setSearchInput, search } = useSearchInput();
  const [sortBy, setSortBy] = useState(query.sortBy || 'order');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || 1);
  const queryClient = useQueryClient();

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(search && { search }),
      }),
    });
  }, [sortBy, sortDirection, search]);

  const { data, fetchNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['template-categories', sortBy, sortDirection, search],
    queryFn: async ({ pageParam }) => {
      return fetchInfinite(
        `/templates/categories?${stringify({
          ...(search && { search }),
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          limit: 20,
          skip: pageParam * 20,
        })}`,
        token,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
    refetchOnMount: true,
  });

  const updateTemplateCategoryMutation = useMutation({
    mutationFn: async ({ _id, values }) => {
      const { data } = await fetcher(`/templates/categories/${_id}`, token, {
        method: 'PUT',
        body: JSON.stringify(values),
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateCategoriesKey });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const deleteTemplateCategoryMutation = useMutation({
    mutationFn: async _id => {
      await fetcher(`/templates/categories/${_id}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateCategoriesKey });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  return (
    <div className="pr-4">
      <div className="flex items-end justify-end gap-x-2">
        <FormElement className="!mb-0">
          <Label htmlFor="search">Search</Label>
          <div className="w-64">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by name, email, ID"
              value={searchInput}
            />
          </div>
        </FormElement>

        <Button href={`/admin/template-categories/new`} appearance="primary">
          New category
        </Button>
      </div>
      <Table.Table>
        <Table.Head>
          <Table.Th onSort={onSort} sortKey="title" sortBy={sortBy} sortDirection={sortDirection}>
            Title
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="enabled" sortBy={sortBy} sortDirection={sortDirection}>
            Enabled
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="slug" sortBy={sortBy} sortDirection={sortDirection}>
            Slug
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="order" sortBy={sortBy} sortDirection={sortDirection}>
            Order
          </Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(category => (
                <TemplateCategoryRow
                  key={category._id}
                  category={category}
                  onChange={values => {
                    updateTemplateCategoryMutation.mutate({
                      _id: category._id,
                      values,
                    });
                  }}
                  onDelete={() => {
                    deleteTemplateCategoryMutation.mutate(category._id);
                  }}
                />
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>
    </div>
  );
};

function TemplateCategoryRow({ category, onChange, onDelete }) {
  const [order, setOrder] = useState(category.order);
  const debouncedOrder = useDebounce(order, 300);

  useEffect(() => {
    if (debouncedOrder === category.order) return;

    onChange({
      order: debouncedOrder,
    });
  }, [debouncedOrder]);

  return (
    <Table.Row key={category._id}>
      <Table.Td>
        <Link href={`/admin/template-categories/${category._id}`}>{category.title}</Link>
      </Table.Td>
      <Table.Td>
        <Circle color={category.enabled ? 'green' : 'red'} />
      </Table.Td>
      <Table.Td>{category.slug}</Table.Td>
      <Table.Td>
        <div className="flex">
          <TextInput
            type="number"
            value={order}
            onChange={e => {
              setOrder(e.target.value);
            }}
          />

          <Button
            onClick={async () => {
              const proceed = await confirm('Delete this template?');

              if (!proceed) return;

              onDelete();
            }}
            appearance="bare"
            className="ml-2"
            size="small"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      </Table.Td>
    </Table.Row>
  );
}

export default Payments;
